/**
 * DEV-260 — copy de apresentação dos campos padrão (placeholder e dica),
 * literal do Figma, por `name` estável do template. Não é estrutura: quais
 * campos aparecem, em que ordem e se são obrigatórios vem da definição; campo
 * sem entrada aqui só não tem placeholder nem dica.
 */
export interface FieldCopy {
  placeholder?: string;
  hint?: string;
}

export const FIELD_COPY: Record<string, FieldCopy> = {
  // Identificação da vaga — 20186:22573 / 22593
  codigo_interno_vaga: {
    placeholder: 'Ex.: RH-2026-014',
    hint: 'Use o código de controle interno da sua empresa. É por ele que identificamos essa vaga antes de criá-la na Eureca.',
  },
  nome_vaga: {
    placeholder: 'Ex.: Estágio em Marketing Digital',
    hint: 'É esse nome que aparece no anúncio para quem se candidata.',
  },
  numero_posicoes: {
    placeholder: 'Ex.: 16',
    hint: 'Quantas pessoas você quer contratar nessa vaga.',
  },
  meta_inscritos: {
    placeholder: 'Ex.: 500',
    hint: 'Quantas inscrições você espera receber. Usamos esse número para dimensionar a divulgação.',
  },
  tipo_contratacao: {
    placeholder: 'Selecione uma opção',
    hint: 'O vínculo que a pessoa contratada vai ter com a empresa.',
  },
  modelo_trabalho: {
    placeholder: 'Selecione uma opção',
    hint: 'Ao escolher presencial ou híbrido, pedimos o local de trabalho em seguida.',
  },
  local_trabalho: { placeholder: 'Selecione uma cidade' },
  requisitos_localidade_estado: {
    placeholder: 'Selecione um estado',
    hint: 'Estados de onde você aceita candidaturas. Sem preencher, aceitamos de todo o Brasil.',
  },
  requisitos_localidade_cidade: {
    // Figma (22593) traz "Selecione um estado" — erro de copy do componente; ver research.md D18.
    placeholder: 'Selecione uma cidade',
    hint: 'Ao indicar uma cidade, só ela vale dentro do estado dela. Os outros estados da lista continuam valendo por inteiro.',
  },
  disponibilidade_mudanca: {
    placeholder: 'Selecione uma opção',
    hint: 'Marque se a vaga exige que a pessoa mude de cidade ou de país.',
  },
  disponibilidade_viagens: {
    placeholder: 'Selecione uma opção',
    hint: 'Marque se a rotina da vaga inclui viagens.',
  },
  turno_trabalho: {
    placeholder: 'Selecione uma opção',
    hint: 'O período do dia em que a pessoa vai trabalhar.',
  },
  carga_horaria_semanal: {
    placeholder: 'Selecione uma opção',
    hint: 'Quantas horas por semana a pessoa vai cumprir.',
  },

  // Remuneração — 20186:22598 / 22606 / 22612 / 22698
  tipo_divulgacao_salario: {
    placeholder: 'Selecione uma opção',
    hint: 'Vagas com salário divulgado costumam receber mais inscrições.',
  },
  valor_minimo: {
    placeholder: 'Ex.: R$ 3.000,00',
    hint: 'Valor bruto mensal, antes dos descontos.',
  },
  valor_maximo: {
    placeholder: 'Ex.: R$ 5.000,00',
    hint: 'Valor bruto mensal, antes dos descontos.',
  },
  valor_exato: { placeholder: 'Ex.: 5.000,00', hint: 'Valor bruto mensal, antes dos descontos.' },
  beneficios: {
    placeholder: 'Selecione uma opção',
    hint: 'Escolha na lista. Se faltar algum, digite o nome e tecle Enter para incluir.',
  },
  pagamentos_adicionais: {
    placeholder: 'Selecione uma opção',
    hint: 'Valores que entram além do salário fixo. Se faltar algum, digite o nome e tecle Enter.',
  },

  // Formação — 20186:22625 / 22657 / 22671
  tipos_formacao: {
    placeholder: 'Selecione uma opção',
    hint: 'Dá para combinar mais de um nível, como técnico e bacharelado.',
  },
  data_minima_formacao: {
    placeholder: 'Selecione mês e ano',
    hint: 'Aceitamos quem conclui o curso a partir dessa data.',
  },
  data_maxima_formacao: {
    placeholder: 'Selecione mês e ano',
    hint: 'Aceitamos quem conclui o curso até essa data.',
  },
  aceita_todos_cursos: {
    hint: 'Ao responder não, você escolhe áreas e cursos específicos em seguida.',
  },
  areas_curso: {
    placeholder: 'Selecione uma ou mais áreas',
    hint: 'Áreas inteiras que você aceita. Elas não limitam a lista de cursos.',
  },
  cursos: {
    placeholder: 'Selecione um ou mais cursos',
    hint: 'Cursos específicos que você aceita. Eles somam com as áreas escolhidas, não substituem.',
  },

  // Perfil do candidato — 20186:22633 / 22645
  idade_minima: {
    placeholder: 'Ex.: 18',
    hint: 'Preencha só quando a idade for exigência legal ou do programa.',
  },
  idade_maxima: {
    placeholder: 'Ex.: 24',
    hint: 'Preencha só quando a idade for exigência legal ou do programa.',
  },
  competencias: {
    placeholder: 'Ex.: Inglês',
    hint: 'O que a pessoa precisa saber para ocupar a vaga. Digite e tecle Enter para adicionar.',
  },
  diferenciais: {
    placeholder: 'Ex.: Excel',
    hint: 'O que conta a favor, mas não elimina quem não tiver. Digite e tecle Enter para adicionar.',
  },
  criterios_diversidade: {
    placeholder: 'Selecione um ou mais critérios',
    hint: 'Use para direcionar a vaga a grupos específicos. Dá para escolher mais de um.',
  },
  tipo_deficiencia: {
    placeholder: 'Selecione um ou mais critérios',
    hint: 'Escolha os tipos que a vaga acolhe. Para não restringir, escolha “Qualquer uma”.',
  },

  // Descritivos — 20186:22913 (sem dica; placeholders são copy final)
  descricao_vaga: {
    placeholder:
      'Explique o que torna essa vaga interessante. Ex.: "Você vai dar suporte à equipe de marketing digital na criação de campanhas para redes sociais."',
  },
  atividades_dia_a_dia: {
    placeholder:
      'Liste as principais tarefas que a pessoa vai realizar. Ex.: "Apoiar a criação de conteúdo para redes sociais e acompanhar métricas de engajamento."',
  },
  observacoes_perfil: {
    placeholder:
      'Descreva as características do profissional ideal para essa vaga. Ex.: "Curioso, com boa comunicação e interesse em aprender ferramentas de design no dia a dia.”',
  },
};

/** Mensagens das listas e avisos das etapas (catálogos de erro 23074/23082/23090). */
export const FIELD_MESSAGES = {
  metaMenorQuePosicoes: 'A meta está menor que o número de posições. Confira se é isso mesmo.',
  idadeEmAberto:
    'Com só uma das idades preenchida, o outro limite fica em aberto. Confira se é isso mesmo.',
  idadeSemExigencia:
    'Filtrar por idade só vale quando há exigência legal ou do programa. Confira antes de seguir.',
  cidadeRepetida: 'Essa cidade já está na lista.',
  localidadeRepetida: 'Essa localidade já está na lista.',
  /** 23074 / DEV-260 AC-28: abrir Cidade dos requisitos sem estado escolhido. */
  estadoAntesDaCidade: 'Escolha o estado antes da cidade.',
  itemRepetido: 'Esse item já está na lista.',
  beneficioVazio: 'Digite o nome do benefício antes de adicionar.',
  beneficioLongo: 'O nome pode ter até 60 caracteres.',
  competenciaVazia: 'Digite uma competência antes de adicionar.',
  competenciaLonga: 'Cada item pode ter até 40 caracteres.',
  adicionarNovo: 'Adicionar novo',
  novoBeneficioPlaceholder: 'Ex.: Clube de vantagens',
  digiteEnter: 'Digite o nome e tecle Enter para adicionar',
  uf: 'UF',
  remover: (item: string) => `Remover ${item}`,
  adicionar: 'Adicionar',
  carregando: 'Carregando...',
  semResultado: 'Nenhum resultado',
  falhaLista: 'Não foi possível carregar as opções.',
  avancar: 'Avançar',
  salvar: 'Salvar',
  editar: 'Editar',
  voltar: 'Voltar',
  sim: 'Sim',
  nao: 'Não',
} as const;
